import { useQuery } from '@tanstack/react-query';
import { auth } from '@dashai/lib/firebase';

export type WorkflowVisibilityStatus = 'loading' | 'loaded' | 'error';

interface WorkflowVisibilityRow {
  id: string;
  name: string;
  is_upcoming: boolean;
  enabled: boolean;
}

// Backoff before attempts 2 and 3. Transient failures (token race at mount,
// network blip) recover here; anything still failing after the last delay is
// terminal — fail closed, never show the unfiltered list.
const RETRY_DELAYS_MS = [1000, 3000];

// A missing build-time env var cannot be fixed by retrying.
class MissingFunctionsUrlError extends Error {}

async function fetchWorkflowVisibility(selectedClient: string): Promise<Set<string>> {
  const fbBase = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL as string | undefined;
  if (!fbBase) {
    console.warn('[useWorkflowVisibility] VITE_FIREBASE_FUNCTIONS_URL not set');
    throw new MissingFunctionsUrlError('VITE_FIREBASE_FUNCTIONS_URL not set');
  }
  const url = `${fbBase.replace(/\/+$/, '')}/dashaiWorkflowVisibility`;

  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('auth token unavailable');

  const response = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_client: selectedClient }),
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const data = await response.json();
  const rows: WorkflowVisibilityRow[] = data.workflows || [];
  return new Set(rows.filter((r) => r.enabled).map((r) => r.id));
}

// True only when the registry has loaded AND the id is enabled. Loading,
// error, null set, and missing id all fail closed.
export function isWorkflowVisible(
  status: WorkflowVisibilityStatus,
  visibleWorkflowIds: Set<string> | null | undefined,
  workflowId: string | null | undefined,
): boolean {
  if (status !== 'loaded' || !visibleWorkflowIds || !workflowId) return false;
  return visibleWorkflowIds.has(workflowId);
}

// The workspace's enabled registry ids. TanStack Query caches the answer per
// acting identity and workspace, and shares one request between every mounted
// caller. The key carries both, so the render that switches workspace or
// identity (impersonation swaps it without a reload) already reads 'loading'
// rather than an answer fetched for someone else. The set is non-null only
// while status is 'loaded'.
export function useWorkflowVisibility(selectedClient: string | null) {
  const uid = auth.currentUser?.uid ?? null;
  const query = useQuery({
    queryKey: ['workflowVisibility', uid, selectedClient],
    queryFn: () => fetchWorkflowVisibility(selectedClient as string),
    enabled: Boolean(selectedClient),
    retry: (failureCount, error) =>
      !(error instanceof MissingFunctionsUrlError) && failureCount < RETRY_DELAYS_MS.length,
    retryDelay: (failureCount) => RETRY_DELAYS_MS[failureCount],
    refetchOnWindowFocus: false,
  });

  // A retry after a terminal error reads as loading again, so a "reload to
  // retry" control shows progress instead of the stale error.
  let status: WorkflowVisibilityStatus = 'loading';
  if (query.isError && !query.isFetching) status = 'error';
  else if (query.isSuccess) status = 'loaded';

  return {
    visibleWorkflowIds: status === 'loaded' ? query.data : null,
    status,
    refetch: query.refetch,
  };
}
